import { type ReactNode, useEffect, useMemo } from 'react';
import { AnalyticsContext } from '@/components/Analytics/AnalyticsContext';
import { type EnqueuedCallback } from '@/components/Analytics/types';
import { useCookieHub } from '@/contexts/cookie-hub-context';
import { useOneTrust } from '@/contexts/onetrust-context';
import { reportError } from '@/helpers/errors/report';
import {
  QDRANT_TECH_AJS_ANONYMOUS_ID_COOKIE_NAME,
  HUBSPOT_UTK_TLD_COOKIE,
  HUBSPOT_UTK_COOKIE,
} from '@/utils/analytics-utils';
import { Cookies } from '@/utils/cookies';
import { analytics, campaignAttributionMiddleware } from './tracking';

export const eventQueue = (function eventQueue(/* Singleton */) {
  let dispatched = false;
  const queue = new Set<[EnqueuedCallback, Date]>();

  return {
    set dispatchQueue(value: boolean) {
      if (value) {
        queue.forEach(([callback, timestamp]) => {
          callback(timestamp);
        });
        queue.clear();
      }
      dispatched = value;
    },
    enqueue: (callback: EnqueuedCallback) => {
      if (dispatched) {
        callback();
      } else {
        queue.add([callback, new Date()]);
      }
    },
  };
})();

function loadSegment() {
  const writeKey = window.__QDRANT_CLOUD__?.segment?.write_key || '';
  let cdnURL = 'https://cdn.segment.com'; // Default fallback URL

  if (window.__QDRANT_CLOUD__?.segment?.src) {
    try {
      cdnURL = new URL(window.__QDRANT_CLOUD__.segment.src).origin;
    } catch (error) {
      // Log error but continue with default CDN URL
      reportError(error, { domain: 'analytics', action: 'parse-segment-cdn-url' });
    }
  }

  analytics.load({
    writeKey,
    cdnURL,
  });
}

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const { hasConsented } = useCookieHub();
  const oneTrust = useOneTrust();
  const { handleIsSegmentLoaded } = oneTrust;

  // Depends on the setter, not on `oneTrust`: that object gets a new identity on every consent
  // change, and re-running this would load Segment and add the middleware a second time.
  useEffect(() => {
    loadSegment();

    void analytics.addSourceMiddleware(campaignAttributionMiddleware);

    analytics
      .ready(() => {
        const qdrantTechAjsAnonymousId = Cookies.get(QDRANT_TECH_AJS_ANONYMOUS_ID_COOKIE_NAME);
        if (qdrantTechAjsAnonymousId) {
          analytics.setAnonymousId(qdrantTechAjsAnonymousId).catch(() => false);
          Cookies.remove(QDRANT_TECH_AJS_ANONYMOUS_ID_COOKIE_NAME);
        }

        window.analytics = analytics;
        handleIsSegmentLoaded(true);
      })
      .catch((err) => {
        reportError(err, { domain: 'analytics', action: 'segment-ready' });
      });
  }, [handleIsSegmentLoaded]);

  useEffect(() => {
    if (!oneTrust.isLoaded || !oneTrust.isSegmentLoaded) {
      return;
    }

    oneTrust.onConsentChanged(async () => {
      oneTrust.registerAndCall();

      await window.analytics.track('onetrust_consent_preference_updated', {
        onetrust_active_groups: window.OnetrustActiveGroups ?? '',
      });
    });
  }, [oneTrust]);

  useEffect(() => {
    eventQueue.enqueue(() =>
      window.analytics.identify({
        [HUBSPOT_UTK_COOKIE]: Cookies.get(HUBSPOT_UTK_COOKIE),
        [`qdrant_tech_${HUBSPOT_UTK_COOKIE}`]: Cookies.get(HUBSPOT_UTK_TLD_COOKIE),
      }),
    );
  }, []);

  const abortController = useMemo(() => new AbortController(), []);
  useEffect(() => {
    document.addEventListener(
      'gtm_cookiehub_analytics_choice',
      () =>
        /** @see https://segment.com/docs/privacy/consent-management/consent-in-unify/#segment-consent-preference-updated-event */
        eventQueue.enqueue(() =>
          window.analytics.track('segment_consent_preference_updated', undefined, {
            context: {
              consent: {
                categoryPreferences: {
                  Advertising: hasConsented('marketing'),
                  Analytics: hasConsented('analytics'),
                  Functional: hasConsented('necessary'),
                  Preferences: hasConsented('preferences'),
                },
              },
            },
          }),
        ),
      { signal: abortController.signal },
    );
    return () => abortController.abort();
  }, [abortController, hasConsented]);

  // Registering the plugin and flushing the queue are both side effects, so neither can run
  // during render.
  useEffect(() => {
    const isReady = oneTrust.isLoaded && oneTrust.isSegmentLoaded;
    if (isReady) {
      oneTrust.registerAndCall();
    }

    eventQueue.dispatchQueue = isReady;
  }, [oneTrust]);

  const value = useMemo(() => ({ enqueueEvent: eventQueue.enqueue }), []);

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};
